import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BuildingIcon, UsersIcon, SparklesIcon, BellIcon, CreditCardIcon, ShieldAlertIcon, CheckCircle2Icon, PlusIcon, TrashIcon, ShieldIcon, UploadIcon, ExternalLinkIcon, MapPinIcon, UserIcon, ShieldCheckIcon, CameraIcon, FileCheckIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useBusiness, BUSINESS_TYPE_LABEL } from './BusinessContext';
import { PremiumSelect } from './PremiumSelect';
import { InviteMemberModal, TeamMember, ROLE_PRESETS, accessSummary } from './InviteMemberModal';
import { UpgradePlanModal } from './UpgradePlanModal';
const INITIAL_MEMBERS: TeamMember[] = [{
  name: 'Fadi',
  role: 'Admin',
  email: 'fadi@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Admin')!.access,
  canViewContact: true
}, {
  name: 'Sara',
  role: 'Sales',
  email: 'sara@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Sales')!.access,
  canViewContact: true
}, {
  name: 'Sales Team',
  role: 'Viewer',
  email: 'team@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Viewer')!.access,
  canViewContact: false
}];
type Section = 'Business Profile' | 'Team Members' | 'Notifications' | 'Plan & Billing' | 'Danger Zone';
export function BusinessSettings() {
  const {
    hub,
    currentPlanId,
    listingCount,
    PACKAGES,
    updateHubProfile,
    subscribed,
    cancelSubscription
  } = useBusiness();
  const [section, setSection] = useState<Section>('Business Profile');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [logo, setLogo] = useState(hub?.logo || '');
  const [location, setLocation] = useState('Dubai, UAE');
  const [mapPin, setMapPin] = useState('');
  const [sellerType, setSellerType] = useState<'personal' | 'business'>('business');
  const [idVerified, setIdVerified] = useState(false);
  const [tradeLicenseUploaded, setTradeLicenseUploaded] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>(INITIAL_MEMBERS);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeInitialPlan, setUpgradeInitialPlan] = useState<string | undefined>();
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  // Shop handle is auto-derived from the name and not editable.
  const handleSlug = (hub?.name ?? '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'yourname';
  const openUpgrade = (planId?: string) => {
    setUpgradeInitialPlan(planId);
    setUpgradeOpen(true);
  };
  const openInvite = () => {
    setEditingMember(null);
    setInviteOpen(true);
  };
  const openEdit = (m: TeamMember) => {
    setEditingMember(m);
    setInviteOpen(true);
  };
  const saveMember = (m: TeamMember) => {
    setMembers(prev => {
      const idx = prev.findIndex(x => x.email === m.email && x.email !== '');
      if (idx >= 0) return prev.map((x, i) => i === idx ? m : x);
      return [...prev, m];
    });
    setInviteOpen(false);
  };
  const removeMember = (email: string) => setMembers(prev => prev.filter(x => x.email !== email));
  const sections: {
    id: Section;
    icon: React.ReactNode;
  }[] = [{
    id: 'Business Profile',
    icon: <BuildingIcon className="w-4 h-4" data-id="element-737" data-mp="27lxivsn2mr47" />
  }, {
    id: 'Team Members',
    icon: <UsersIcon className="w-4 h-4" data-id="element-738" data-mp="27o9xpwxj8ld1" />
  }, {
    id: 'Notifications',
    icon: <BellIcon className="w-4 h-4" data-id="element-739" data-mp="1176cxaj3cg3b" />
  }, {
    id: 'Plan & Billing',
    icon: <CreditCardIcon className="w-4 h-4" data-id="element-740" data-mp="w578dxbm7d4s" />
  }, {
    id: 'Danger Zone',
    icon: <ShieldAlertIcon className="w-4 h-4" data-id="element-741" data-mp="2k2ojcpcxd46n" />
  }];
  return <div className="w-full max-w-6xl mx-auto pb-24" data-id="element-742" data-mp="2ouq1ut0orr54">
      <div className="mb-6" data-id="element-743" data-mp="2ovhnbb0kk8ho">
        <h2 className="text-2xl font-display font-bold text-dark-navy" data-id="element-744" data-mp="2b2s2h9ws19m5">
          Settings
        </h2>
        <p className="text-sm text-slate-500" data-id="element-745" data-mp="2f43s3ffnhuuc">
          Manage your Business Hub profile, team, and preferences.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-746" data-mp="32v97jthoil44">
        {/* Section nav */}
        <div className="lg:w-56 flex-shrink-0" data-id="element-747" data-mp="37taregmxiogn">
          <div className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar" data-id="element-748" data-mp="37qy8a05fq820">
            {sections.map(s => <button key={s.id} onClick={() => setSection(s.id)} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${section === s.id ? 'bg-white text-primary-indigo shadow-soft border border-slate-100' : 'text-slate-600 hover:bg-white/60'} ${s.id === 'Danger Zone' && section !== s.id ? 'text-red-600' : ''}`} data-id="element-749" data-mp="2yscj3m5gjus9">
                <span className={section === s.id ? 'text-primary-indigo' : s.id === 'Danger Zone' ? 'text-red-500' : 'text-slate-400'} data-id="element-750" data-mp="1atjzd7z4spm3">
                  {s.icon}
                </span>
                {s.id}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <motion.div key={section} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex-1 space-y-6" data-id="element-751" data-mp="15v8octx3o92a">
          {section === 'Business Profile' && <Card data-id="element-752" data-mp="8ge90iz2l6z3">
              <SectionTitle title="Business Profile" desc="This information appears across TALAB." data-id="element-753" data-mp="8gwfe8fg51kq" />
              <div className="space-y-6" data-id="element-754" data-mp="28g9ru8ug4hfr">
                {/* Step 1 — choose seller type first (drives the name field below) */}
                <div data-id="element-755" data-mp="2i8fi9e51uqsu">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-756" data-mp="2i7xd1s0et28s">
                    I'm selling as
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-757" data-mp="2mbvbe0hwu90s">
                    {([{
                  id: 'personal',
                  label: 'Personal Seller',
                  desc: 'Selling as an individual',
                  icon: UserIcon
                }, {
                  id: 'business',
                  label: 'Business',
                  desc: 'Registered company or shop',
                  icon: BuildingIcon
                }] as const).map(opt => {
                  const active = sellerType === opt.id;
                  return <button key={opt.id} type="button" onClick={() => setSellerType(opt.id)} className={`text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${active ? 'border-primary-indigo bg-primary-indigo/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-758" data-mp="3k3tnb1lotadd">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-primary-indigo text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-759" data-mp="31f55wpmexn6c">
                            <opt.icon className="w-5 h-5" data-id="element-760" data-mp="umm7w1mvhq08" />
                          </div>
                          <div data-id="element-761" data-mp="qk7rdo6of2c4">
                            <p className="font-semibold text-slate-900 text-sm" data-id="element-762" data-mp="qm296tmniebz">
                              {opt.label}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-763" data-mp="1duuh9f49iyy0">
                              {opt.desc}
                            </p>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                <div className="flex items-start gap-6" data-id="element-764" data-mp="q5rpvfjttrqb">
                  <div className="flex-shrink-0" data-id="element-765" data-mp="q46vbpxii8sg">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-766" data-mp="v54ea4qcj6v0">
                      {sellerType === 'business' ? 'Shop Logo' : 'Profile Photo'}
                    </label>
                    <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer hover:border-primary-indigo/50 hover:bg-indigo-50/30 transition-colors" data-id="element-767" data-mp="m46j8bxvy9ok">
                      {logo ? <img src={logo} alt="Shop Logo" className="w-full h-full object-cover" data-id="element-768" data-mp="2qm4c3ye59595" /> : <>
                          <UploadIcon className="w-6 h-6 text-slate-400 mb-1 group-hover:text-primary-indigo" data-id="element-769" data-mp="2un7qeggjmt89" />
                          <span className="text-[10px] font-medium text-slate-500 group-hover:text-primary-indigo" data-id="element-770" data-mp="39bb1235j6t3e">
                            Upload
                          </span>
                        </>}
                      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      const url = URL.createObjectURL(e.target.files[0]);
                      setLogo(url);
                    }
                  }} data-id="element-771" data-mp="3iay1omqfruve" />
                    </div>
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-772" data-mp="2vm2ewu2v8suq">
                    {sellerType === 'business' ? <div data-id="element-773" data-mp="2qoane164v8r2">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-774" data-mp="2qp26wdmghg0z">
                          Business Name
                        </label>
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${tradeLicenseUploaded ? 'bg-slate-50 border-slate-200 text-slate-900 font-medium' : 'bg-slate-50 border-slate-200 border-dashed text-slate-400'}`} data-id="element-775" data-mp="3jbn07jmhs276">
                          {tradeLicenseUploaded ? <>
                              <FileCheckIcon className="w-4 h-4 text-success shrink-0" data-id="element-776" data-mp="65wm8vufjjf5" />
                              <span className="flex-1 truncate" data-id="element-777" data-mp="dgsx4l74bksi">
                                {hub?.name}
                              </span>
                              <span className="text-[11px] font-medium text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full" data-id="element-778" data-mp="ignlagzidw9v">
                                From trade license
                              </span>
                            </> : <>
                              <UploadIcon className="w-4 h-4 shrink-0" data-id="element-779" data-mp="wdkntfozp26l" />
                              <span data-id="element-780" data-mp="11dfbladgxbnf">
                                Upload your trade license to auto-fill the
                                business name
                              </span>
                            </>}
                        </div>
                      </div> : <Field label="Full Name" defaultValue={hub?.name ?? ''} onChange={val => updateHubProfile({
                  name: val
                })} data-id="element-781" data-mp="1g1tcvgv8ks1v" />}
                    <div data-id="element-782" data-mp="3pz7hvnozeaq3">
                      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-783" data-mp="3pzh0urblzhma">
                        Shop Handle
                      </label>
                      <div className="flex items-center rounded-xl border border-slate-200 bg-slate-100 overflow-hidden" data-id="element-784" data-mp="3qfzd3npc8wi">
                        <span className="px-4 py-3 text-slate-500 text-sm font-medium" data-id="element-785" data-mp="3uwh4kvkzk8a8">
                          talab.app/
                        </span>
                        <span className="flex-1 px-1 py-3 text-slate-700 text-sm font-semibold truncate" data-id="element-786" data-mp="8qbv6fm8wu0z">
                          {handleSlug}
                        </span>
                      </div>
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400" data-id="element-787" data-mp="2ahp034o7r1x1">
                        <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-788" data-mp="20dh0md62f896" />
                        Auto-generated from your name and can't be changed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Verification */}
                <div className="rounded-2xl border border-slate-200 p-5 space-y-5" data-id="element-789" data-mp="1qlkxgnp4gry3">
                  {/* Account verification — required for both */}
                  <div data-id="element-790" data-mp="1gp83dopo0c2t">
                    <div className="flex items-center justify-between mb-2" data-id="element-791" data-mp="1gny631qmz7x8">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-792" data-mp="1lpngqg9y5jks">
                        Account Verification
                      </label>
                      <span className="text-xs font-medium text-error" data-id="element-793" data-mp="1cnwe5kapdsv5">
                        Required
                      </span>
                    </div>
                    {idVerified ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl" data-id="element-794" data-mp="371xb1zmp05sl">
                        <CheckCircle2Icon className="w-6 h-6 text-success shrink-0" data-id="element-795" data-mp="16zfq4pn73813" />
                        <div className="flex-1" data-id="element-796" data-mp="120l3tdq0r1c8">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-797" data-mp="121w8dfragd4e">
                            ID submitted for verification
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-798" data-mp="t0fxl6wka8zn">
                            We'll review and confirm your identity shortly.
                          </p>
                        </div>
                        <button type="button" onClick={() => setIdVerified(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700" data-id="element-799" data-mp="j9l9dwzr8zbk">
                          Replace
                        </button>
                      </div> : <div className="rounded-xl border border-slate-200 border-dashed p-4" data-id="element-800" data-mp="b90mccr9rgi">
                        <div className="flex items-center gap-3 mb-3" data-id="element-801" data-mp="dlh0l2v7trg">
                          <ShieldCheckIcon className="w-5 h-5 text-primary-indigo" data-id="element-802" data-mp="2by2uu4ztmn4q" />
                          <p className="text-sm text-slate-600" data-id="element-803" data-mp="2gwcm44ifd3l2">
                            Verify your identity by scanning or uploading your
                            ID.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-id="element-804" data-mp="2pvzqmz6gapic">
                          <button type="button" onClick={() => setIdVerified(true)} className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-805" data-mp="1t9e7lcdv85ps">
                            <CameraIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo mb-1.5 transition-colors" data-id="element-806" data-mp="19kvptfv6vul4" />
                            <span className="text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-807" data-mp="1ehdt0bavfkvc">
                              Scan ID
                            </span>
                          </button>
                          <button type="button" onClick={() => setIdVerified(true)} className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-808" data-mp="2qm6brp5a3p9w">
                            <UploadIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo mb-1.5 transition-colors" data-id="element-809" data-mp="1ty8b24mi4ruo" />
                            <span className="text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-810" data-mp="22wc19e8fmcst">
                              Upload ID
                            </span>
                          </button>
                        </div>
                      </div>}
                  </div>

                  {/* Trade license — business only */}
                  {sellerType === 'business' && <div data-id="element-811" data-mp="3qvxnuialjnbu">
                      <div className="flex items-center justify-between mb-2" data-id="element-812" data-mp="3cutkdxnncp0m">
                        <label className="block text-sm font-medium text-slate-700" data-id="element-813" data-mp="3cwxyt9j696f5">
                          Trade License
                        </label>
                        <span className="text-xs font-medium text-error" data-id="element-814" data-mp="9f5zfa2phk6x">
                          Required for business
                        </span>
                      </div>
                      {tradeLicenseUploaded ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl" data-id="element-815" data-mp="3vtfm9eqzmk5l">
                          <FileCheckIcon className="w-6 h-6 text-success shrink-0" data-id="element-816" data-mp="4l1qil9vr237" />
                          <div className="flex-1" data-id="element-817" data-mp="sbnvu4si289i">
                            <p className="font-medium text-slate-900 text-sm" data-id="element-818" data-mp="x3yy3eapq658">
                              Trade license uploaded
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-819" data-mp="2t8omsrbiujgt">
                              Pending review by the Talab team.
                            </p>
                          </div>
                          <button type="button" onClick={() => setTradeLicenseUploaded(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700" data-id="element-820" data-mp="2jeo6dtashrss">
                            Replace
                          </button>
                        </div> : <button type="button" onClick={() => {
                  setTradeLicenseUploaded(true);
                  // Trade license is the source of truth for the legal
                  // business name — auto-fill it on upload.
                  updateHubProfile({
                    name: 'Easylease Rentals LLC'
                  });
                }} className="w-full flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group text-left" data-id="element-821" data-mp="336ull02179hg">
                          <UploadIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo transition-colors shrink-0" data-id="element-822" data-mp="l005qrkrg0cm" />
                          <div data-id="element-823" data-mp="pwgv1p3p4kra">
                            <span className="block text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-824" data-mp="uxel0nuy6j3c">
                              Upload a valid trade license
                            </span>
                            <span className="block text-xs text-slate-400 mt-0.5" data-id="element-825" data-mp="2wpj7hjtogo6o">
                              PDF or image of your active UAE trade license —
                              we'll read the business name from it
                            </span>
                          </div>
                        </button>}
                    </div>}

                  {/* Verification status summary */}
                  <div className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 ${idVerified && (sellerType === 'personal' || tradeLicenseUploaded) ? 'bg-success/5 text-success' : 'bg-amber-50 text-amber-700'}`} data-id="element-826" data-mp="2ufpqzmdyfzku">
                    {idVerified && (sellerType === 'personal' || tradeLicenseUploaded) ? <>
                        <CheckCircle2Icon className="w-4 h-4" data-id="element-827" data-mp="3d6h5f2csug63" />
                        <span className="font-medium" data-id="element-828" data-mp="3i626pjffz0lz">
                          Verification complete — your account is fully set up.
                        </span>
                      </> : <>
                        <ShieldAlertIcon className="w-4 h-4" data-id="element-829" data-mp="1nv6u8614z1dx" />
                        <span className="font-medium" data-id="element-830" data-mp="1gbe4h0imomcy">
                          {sellerType === 'business' ? 'Verify your ID and upload your trade license to activate selling.' : 'Verify your ID to activate selling.'}
                        </span>
                      </>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-831" data-mp="1z86xcvnvvl6r">
                  <PremiumSelect label="Business Type" value={BUSINESS_TYPE_LABEL[hub?.type ?? 'generic']} options={Object.values(BUSINESS_TYPE_LABEL)} data-id="element-832" data-mp="1u9cig4t1egrx" />
                  <div data-id="element-833" data-mp="1kawmnoj2ejc3">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-834" data-mp="1kdslrg6nfe24">
                      Location Name
                    </label>
                    <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Al Quoz, Dubai, UAE" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-835" data-mp="8v7zox5t9kba" />
                  </div>
                </div>

                {/* Map pin */}
                <div data-id="element-836" data-mp="2deh8fhsshe3c">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-837" data-mp="2ddz7w8z3gdpm">
                    Pin Location on Google Maps
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2" data-id="element-838" data-mp="2i6s6sg7vsrts">
                    <div className="relative flex-1" data-id="element-839" data-mp="24f3vug9dc9ee">
                      <MapPinIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-840" data-mp="29c46z5l2s2cd" />
                      <input value={mapPin} onChange={e => setMapPin(e.target.value)} placeholder="Paste a Google Maps link or coordinates (e.g. 25.1193, 55.2010)" className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-841" data-mp="1uf3c4t7l7r9o" />
                    </div>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}`} target="_blank" rel="noreferrer" className="px-4 py-3 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:border-primary-indigo hover:text-primary-indigo transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-842" data-mp="1krr2zm3f13am">
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-843" data-mp="2iezrlpypqsgu" /> Open Google Maps
                    </a>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5" data-id="element-844" data-mp="2dhhfxrtb9z55">
                    Drop a pin in Google Maps, then paste the link or
                    coordinates here. The preview below updates automatically.
                  </p>

                  {/* Map preview */}
                  <div className="mt-3 rounded-2xl overflow-hidden border border-slate-200 shadow-soft" data-id="element-845" data-mp="kj11yxf45rqe">
                    <iframe title="Business location preview" className="w-full h-64" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps?q=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}&output=embed`} data-id="element-846" data-mp="pj708wfw2chg" />
                  </div>
                </div>
                <Field label="Contact Email" defaultValue="business@email.com" data-id="element-847" data-mp="6tqvty131zvr" />
                <Field label="Phone" defaultValue="+971 4 000 0000" data-id="element-848" data-mp="1dakoorax5nx5" />
                <TextAreaField label="About" defaultValue="Premium vehicle rentals across the UAE." data-id="element-849" data-mp="1i5rp9q8w6chu" />
              </div>
              <SaveBar onSave={() => updateHubProfile({
            logo,
            shopSlug: handleSlug
          })} data-id="element-850" data-mp="2xq0qx2cm0ba6" />
            </Card>}

          {section === 'Team Members' && <Card data-id="element-851" data-mp="2j1w69stzxpor">
              <div className="flex items-center justify-between mb-5" data-id="element-852" data-mp="2j2fozbrh0i3u">
                <SectionTitle title="Team Members" desc="Invite and manage who can access this hub." noMargin data-id="element-853" data-mp="2ez9jw7g3gwyy" />
                <button onClick={openInvite} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-854" data-mp="251ld2j57rz0f">
                  <PlusIcon className="w-4 h-4" data-id="element-855" data-mp="39yrwy806ybv" /> Invite
                </button>
              </div>
              <div className="space-y-3" data-id="element-856" data-mp="1xq6wz7nvy930">
                {members.map(m => <div key={m.email || m.name} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3" data-id="element-857" data-mp="22gx5gtrlys1x">
                    <div className="flex items-center gap-3" data-id="element-858" data-mp="1squ8yjla2vyu">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple text-white font-bold flex items-center justify-center text-sm" data-id="element-859" data-mp="1sp13h7meihsf">
                        {m.name.charAt(0)}
                      </div>
                      <div data-id="element-860" data-mp="26jk1z7z8515g">
                        <div className="font-medium text-dark-navy text-sm" data-id="element-861" data-mp="26i8zdbhilzfx">
                          {m.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-862" data-mp="v3vco9yhzq1h">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-863" data-mp="103rf44rjr9ex">
                      <span className="text-xs font-medium text-slate-600 px-2 py-1 bg-white border border-slate-200 rounded-md" data-id="element-864" data-mp="3eahceyu1yhrb">
                        {m.role}
                      </span>
                      <span className="hidden sm:inline text-xs text-slate-400" data-id="element-865" data-mp="3nb5npls2a40i">
                        {accessSummary(m.access)}
                      </span>
                      <button onClick={() => openEdit(m)} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md" title="Edit access" data-id="element-866" data-mp="3ja29f3pnwg1e">
                        <ShieldIcon className="w-4 h-4" data-id="element-867" data-mp="34ctfq3cvz06i" />
                      </button>
                      {m.role !== 'Admin' && <button onClick={() => removeMember(m.email)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md" title="Remove" data-id="element-868" data-mp="2zk0azfhf3ruf">
                          <TrashIcon className="w-4 h-4" data-id="element-869" data-mp="2wpy5b624etic" />
                        </button>}
                    </div>
                  </div>)}
              </div>
            </Card>}

          {section === 'Notifications' && <Card data-id="element-870" data-mp="3aopfihdv3v17">
              <SectionTitle title="Notifications" desc="Choose what you want to be alerted about." data-id="element-871" data-mp="3ap7jk06rndjo" />
              <div className="space-y-1" data-id="element-872" data-mp="3flxz8mens74k">
                <Toggle label="New leads" desc="When Talab qualifies a new buyer." on data-id="element-873" data-mp="3kmlmqy3oi5u1" />
                <Toggle label="Hot leads (90%+ intent)" desc="High-intent buyers ready to purchase." on data-id="element-874" data-mp="3pfybr9c9senb" />
                <Toggle label="New messages" desc="Customer replies in Conversations." on data-id="element-875" data-mp="2ap6tl5vr9tq" />
                <Toggle label="Weekly demand report" desc="Search and interest signal summary." data-id="element-876" data-mp="hcoyz8whytmu" />
              </div>
            </Card>}

          {section === 'Plan & Billing' && <Card data-id="element-877" data-mp="7e9is0z0d4s4">
              <SectionTitle title="Plan & Billing" desc="Manage your subscription and payment method." data-id="element-878" data-mp="7ft07uwehlvh" />
              <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-6 text-white mb-8" data-id="element-879" data-mp="101lshak2li40">
                <div className="flex items-center justify-between mb-4" data-id="element-880" data-mp="v4vdkqk04sk6">
                  <div data-id="element-881" data-mp="qc2as883fuaq">
                    <div className="text-sm text-indigo-200" data-id="element-882" data-mp="qbj09ckrw86q">Current Plan</div>
                    <div className="text-2xl font-display font-bold" data-id="element-883" data-mp="mc1tmftm14zz">
                      {currentPlan.name}
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold" data-id="element-884" data-mp="1iuzf8sdmqi8j">
                    Active
                  </span>
                </div>

                <div className="mb-4" data-id="element-885" data-mp="2frtsnrase7hd">
                  <div className="flex items-center justify-between text-sm mb-1.5" data-id="element-886" data-mp="2as8kcq9q8mrz">
                    <span className="text-indigo-100" data-id="element-887" data-mp="2aul9bndwjxej">Listing Usage</span>
                    <span className="font-medium" data-id="element-888" data-mp="2pr2jibacv5zi">
                      {listingCount} of{' '}
                      {currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit}{' '}
                      listings
                    </span>
                  </div>
                  <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden" data-id="element-889" data-mp="38fzfcjlei6ju">
                    <div className="h-full bg-white/80 rounded-full transition-all" style={{
                  width: `${Math.min(100, listingCount / (currentPlan.listingLimit === Infinity ? Math.max(listingCount, 1) : currentPlan.listingLimit) * 100)}%`
                }} data-id="element-890" data-mp="38htq552ykia7" />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-indigo-100" data-id="element-891" data-mp="3w1jk9vavhpsh">
                  <span data-id="element-892" data-mp="4uyv6h4dory3">
                    {currentPlan.price === 0 ? 'Free forever' : `AED ${currentPlan.price.toLocaleString()} / month · Renews soon`}
                  </span>
                  {currentPlan.price > 0 && <button className="text-white hover:text-indigo-200 underline underline-offset-2 transition-colors" data-id="element-893" data-mp="3dbbl1oaanjhk">
                      Update Payment Method
                    </button>}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4" data-id="element-894" data-mp="38dif8wcwkrht">
                <div data-id="element-895" data-mp="2ug3kdc3vrrig">
                  <div className="font-bold text-dark-navy" data-id="element-896" data-mp="2zcu1m2rf8t5g">
                    Need more listings?
                  </div>
                  <p className="text-sm text-slate-500" data-id="element-897" data-mp="14x50g64cu69c">
                    Compare plans and unlock a higher listing capacity.
                  </p>
                </div>
                <button onClick={() => openUpgrade()} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors whitespace-nowrap" data-id="element-898" data-mp="w4l2l7vw05pk">
                  {currentPlan.price > 0 ? 'Change Plan' : 'Upgrade Plan'}
                </button>
              </div>

              {(subscribed || currentPlan.price > 0) && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-899" data-mp="1eu9qwi58gnvy">
                  {!cancelOpen ? <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-900" data-mp="19uwvh90aq8hr">
                      <div data-id="element-901" data-mp="3gxma51ng18q">
                        <div className="font-medium text-dark-navy text-sm" data-id="element-902" data-mp="3h78dhjkpo95">
                          Cancel subscription
                        </div>
                        <p className="text-sm text-slate-500" data-id="element-903" data-mp="3kj1r8dnmsvti">
                          You'll keep {currentPlan.name} access until the end of
                          your billing period, then move to the Free plan.
                        </p>
                      </div>
                      <button onClick={() => setCancelOpen(true)} className="px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap" data-id="element-904" data-mp="31sdytvergqp">
                        Cancel subscription
                      </button>
                    </div> : <div className="rounded-2xl border border-red-100 bg-red-50 p-4" data-id="element-905" data-mp="m169hg8sczjb">
                      <div className="font-bold text-red-900 text-sm mb-1" data-id="element-906" data-mp="2iswq90te06rs">
                        Cancel your {currentPlan.name} plan?
                      </div>
                      <p className="text-sm text-red-700 mb-4" data-id="element-907" data-mp="2ns9jq4erkc3c">
                        Your listing limit will drop to the Free plan (1
                        listing). Listings beyond the free limit will be paused
                        until you upgrade again.
                      </p>

                      <div className="mb-4" data-id="element-908" data-mp="r3vqcvbvc0jf">
                        <label className="block text-sm font-medium text-red-900 mb-2" data-id="element-909" data-mp="m5kekf20jfyk">
                          Please tell us why you're leaving
                          <span className="text-red-500" data-id="element-910" data-mp="3ulnjwe5nksjq"> *</span>
                        </label>
                        <div className="space-y-2" data-id="element-911" data-mp="3ql3p3h8jy10r">
                          {['Too expensive', "I'm not using it enough", 'Missing features I need', 'Switching to another tool', 'Just a temporary pause', 'Other'].map(reason => <label key={reason} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white cursor-pointer transition-colors text-sm ${cancelReason === reason ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-912" data-mp="36tp5vmetkj2w">
                              <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={e => setCancelReason(e.target.value)} className="accent-red-600" data-id="element-913" data-mp="1icf9llxfvpyo" />
                              <span className="text-slate-700" data-id="element-914" data-mp="kndp8xt2h0z8">{reason}</span>
                            </label>)}
                        </div>
                        {cancelReason === 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Tell us more…" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-915" data-mp="5n855nsmhess" />}
                        {cancelReason && cancelReason !== 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Anything else you'd like to add? (optional)" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-916" data-mp="39yk7qk39z76z" />}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end" data-id="element-917" data-mp="1fpeytd5brzf3">
                        <button onClick={() => {
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-white rounded-lg" data-id="element-918" data-mp="1kiq61fe1ui7j">
                          Keep my plan
                        </button>
                        <button disabled={!cancelReason || cancelReason === 'Other' && !cancelNote.trim()} onClick={() => {
                  cancelSubscription();
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-919" data-mp="15ttsj2t84czj">
                          Submit & cancel
                        </button>
                      </div>
                    </div>}
                </div>}
            </Card>}

          {section === 'Danger Zone' && <div className="bg-red-50 border border-red-100 rounded-2xl p-6 space-y-4" data-id="element-920" data-mp="3vxwz3a1vrino">
              <SectionTitle title="Danger Zone" desc="These actions are permanent." noMargin danger data-id="element-921" data-mp="3r04xfobfoi6a" />
              <div className="flex items-center justify-between pt-2" data-id="element-922" data-mp="1wmm5cu069rsd">
                <div data-id="element-923" data-mp="c3s51mwuphdp">
                  <div className="font-medium text-red-900" data-id="element-924" data-mp="c4l0vsu9umh2">
                    Deactivate Business Hub
                  </div>
                  <div className="text-sm text-red-700" data-id="element-925" data-mp="h1tkzyyzd0ky">
                    Temporarily hide your hub and listings.
                  </div>
                </div>
                <button className="px-4 py-2 bg-white border border-red-200 text-red-700 text-sm font-medium rounded-lg hover:bg-red-50" data-id="element-926" data-mp="7c1ov0b8fzed">
                  Deactivate
                </button>
              </div>
              <div className="h-px bg-red-100" data-id="element-927" data-mp="3tl2un0669u9y" />
              <div className="flex items-center justify-between" data-id="element-928" data-mp="3thouzzow7z67">
                <div data-id="element-929" data-mp="3fltg04xm7883">
                  <div className="font-medium text-red-900" data-id="element-930" data-mp="3fmd27xuasirx">
                    Delete Business Hub
                  </div>
                  <div className="text-sm text-red-700" data-id="element-931" data-mp="1dt5a1ntl66om">
                    Permanently remove your hub, catalog, and leads.
                  </div>
                </div>
                <button className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex items-center gap-2" data-id="element-932" data-mp="1ipvz30ndccm6">
                  <TrashIcon className="w-4 h-4" data-id="element-933" data-mp="3j8divpb270of" /> Delete
                </button>
              </div>
            </div>}
        </motion.div>
      </div>

      {inviteOpen && <InviteMemberModal member={editingMember} onClose={() => setInviteOpen(false)} onSave={saveMember} data-id="element-934" data-mp="3a6xcdsnd7iph" />}

      {upgradeOpen && <UpgradePlanModal initialPlan={upgradeInitialPlan} onClose={() => setUpgradeOpen(false)} data-id="element-935" data-mp="2vgg903r66ipy" />}
    </div>;
}
function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-soft" data-id="element-936" data-mp="2qiwud2qk83xh">
      {children}
    </div>;
}
function SectionTitle({
  title,
  desc,
  noMargin,
  danger
}: {
  title: string;
  desc: string;
  noMargin?: boolean;
  danger?: boolean;
}) {
  return <div className={noMargin ? '' : 'mb-5'} data-id="element-937" data-mp="2hkck8pz2f2kv">
      <h3 className={`text-lg font-bold ${danger ? 'text-red-900' : 'text-dark-navy'}`} data-id="element-938" data-mp="1xvwy5h8bhhy0">
        {title}
      </h3>
      <p className={`text-sm ${danger ? 'text-red-700' : 'text-slate-500'}`} data-id="element-939" data-mp="1szg7ogdnarhr">
        {desc}
      </p>
    </div>;
}
function Field({
  label,
  defaultValue,
  onChange
}: {
  label: string;
  defaultValue?: string;
  onChange?: (val: string) => void;
}) {
  return <div data-id="element-940" data-mp="2gusryrg9jubb">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-941" data-mp="2gu9i7y0roc8d">
        {label}
      </label>
      <input defaultValue={defaultValue} onChange={e => onChange?.(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-942" data-mp="2bvpvvwi9gyfh" />
    </div>;
}
function TextAreaField({
  label,
  defaultValue
}: {
  label: string;
  defaultValue?: string;
}) {
  return <div data-id="element-943" data-mp="2vlrsrgauxekc">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-944" data-mp="2vm1nfh6uk9wd">
        {label}
      </label>
      <textarea rows={3} defaultValue={defaultValue} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 text-sm resize-none" data-id="element-945" data-mp="32p815sn2a1ye" />
    </div>;
}
function SaveBar({
  onSave
}: {
  onSave?: () => void;
}) {
  return <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-slate-100" data-id="element-946" data-mp="3hdvxi14qefpe">
      <button className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-947" data-mp="3ldd2yuk5rcuk">
        Cancel
      </button>
      <button onClick={onSave} className="px-5 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-948" data-mp="3qfc3zxsfdxoc">
        <CheckCircle2Icon className="w-4 h-4" data-id="element-949" data-mp="459lzq4qha9g" /> Save Changes
      </button>
    </div>;
}
function Toggle({
  label,
  desc,
  on
}: {
  label: string;
  desc: string;
  on?: boolean;
}) {
  const [enabled, setEnabled] = useState(!!on);
  return <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0" data-id="element-950" data-mp="n21hdabsgueg">
      <div className="pr-4" data-id="element-951" data-mp="ib1i3yf7608s">
        <div className="font-medium text-dark-navy text-sm" data-id="element-952" data-mp="iarq65aldj0g">{label}</div>
        <div className="text-xs text-slate-500" data-id="element-953" data-mp="e9gb2tpcgg6i">{desc}</div>
      </div>
      <button onClick={() => setEnabled(e => !e)} role="switch" aria-checked={enabled} aria-label={label} className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${enabled ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-954" data-mp="e7llh234nzw3">
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} data-id="element-955" data-mp="11xfybjng5ogc" />
      </button>
    </div>;
}